import { useCallback, useState } from 'react';
import type { FC } from 'react';
import { Flex, Spinner } from '@chakra-ui/react';
import pick from 'lodash-es/pick';
import { FormattedMessage, useIntl } from 'react-intl';
import { useUpdateProductItem } from '../../../../bff-api/baskets/useUpdateProductItem';
import { ConditionalRender } from '../../../../components/conditionally-render/ConditionalRender';
import { useExtendGetOffers } from '../../../../components/extend-warranty';
import { useExtendWarranty } from '../../../../components/extend-warranty/useExtendWarranty';
import { DEFAULT_CURRENCY } from '../../../../constants';
import { useGTM } from '../../../../hooks';
import { GTMAddToBagEvent } from '../../../../hooks/gtm/model/events/GTMAddToBagEvent';
import { Button, Text } from '../../../../ui-components';
import { isBopis, isSDD } from '../../../../utils/product-utils';
import type { ProductItemEditor } from '../../../../bff-api/baskets/model';
import type { AdhElement } from '../../../../types/extend';

export interface UpsellButtonProps {
    productItem: ProductItemEditor;
    isExtendEligible?: boolean;
    hasEnoughStock?: boolean;
}

export const UpsellButton: FC<UpsellButtonProps> = ({
    productItem,
    isExtendEligible,
    hasEnoughStock,
}: UpsellButtonProps) => {
    const intl = useIntl();
    const updateProductItem = useUpdateProductItem();
    const extend = useExtendWarranty();
    const { onAddToBag } = useGTM();

    const showExtendUpSell =
        extend.isSdkLoaded &&
        extend.isCartUpsellEnabled &&
        !!isExtendEligible &&
        !!hasEnoughStock &&
        !isBopis(productItem) &&
        !isSDD(productItem);

    const { data: plans } = useExtendGetOffers(productItem.productId, {
        enabled: showExtendUpSell,
    });

    const [, secondOffer] = plans ?? [];
    const [currentOffer, setCurrentOffer] = useState<
        Pick<AdhElement, 'currencyCode' | 'price'> | undefined
    >(secondOffer);

    const offer = currentOffer ?? secondOffer;

    const handleOnUpsellClick = useCallback(() => {
        if (productItem.productId) {
            extend.openModal(productItem.productId, (plan) => {
                if (!plan) {
                    return;
                }
                setCurrentOffer((offerState) => ({
                    currencyCode: DEFAULT_CURRENCY,
                    ...offerState,
                    price: plan.price,
                }));
                extend.addPlan(productItem, plan);

                const item = {
                    ...pick(productItem, [
                        'itemId',
                        'quantity',
                        'productId',
                        'productName',
                        'c_extendPlan',
                        'c_ExtendAssociation',
                    ]),
                    c_addedToCartAt: new Date().toISOString(),
                    c_updatedInCartAt: new Date().toISOString(),
                };

                updateProductItem.mutate(item, {
                    onSuccess: (basket) => {
                        const extendItem = extend.buildExtendProductFromProductItem(basket, item)!;
                        extendItem.c_quantityAddedToBag = item.quantity;

                        onAddToBag(new GTMAddToBagEvent('BAG_UP_SELL', basket, extendItem));
                    },
                });
            });
        }
    }, [onAddToBag, updateProductItem, extend, productItem]);

    return (
        <ConditionalRender visibility={!!showExtendUpSell}>
            <Flex width="100%" gap={5} data-testid={`cart-item-upsell-${productItem.productId}`}>
                <Button
                    id={`cart-item-${productItem.productId}-upsell-button`}
                    variant="secondary"
                    onClick={handleOnUpsellClick}
                    isDisabled={updateProductItem.isLoading}
                    padding="8px"
                >
                    <Flex gap={2} alignItems="center">
                        <Text textTransform="none" style="regular">
                            <FormattedMessage
                                id="cart.item.extend.upsell"
                                defaultMessage="Add accident protection for {amount}"
                                values={{
                                    amount:
                                        !!offer?.currencyCode && !!offer?.price
                                            ? intl.formatNumber(offer.price / 100, {
                                                  style: 'currency',
                                                  currency: offer.currencyCode,
                                              })
                                            : '',
                                }}
                            />
                        </Text>
                        {updateProductItem.isLoading ? (
                            <Spinner size="xs" color="slt-pepper.1000" />
                        ) : null}
                    </Flex>
                </Button>
            </Flex>
        </ConditionalRender>
    );
};

UpsellButton.defaultProps = {
    isExtendEligible: false,
    hasEnoughStock: true,
};
